import React, { useMemo, useState } from "react";
import { FileText, Download, ExternalLink, Calendar } from "lucide-react";
import { computeAcademicYear } from "@/lib/academicYear";

/**
 * Shared "Academics" page rendered on every public institute site.
 * Lists documents with download links, filtered by academic year (default = current).
 */
export default function PublicAcademicsPage({
  docs = [],
  theme = "amber",
  testidPrefix = "ac",
}) {
  const THEMES = {
    amber: { accent: "#D48F1A", chip: "bg-amber-50 text-amber-900", border: "border-amber-200" },
    navy: { accent: "#D4A017", chip: "bg-[#001F3F]/5 text-[#001F3F]", border: "border-slate-200" },
    cream: { accent: "#AF734A", chip: "bg-[#FBF6EE] text-[#AF734A]", border: "border-[#E5D9C6]" },
    saffron: { accent: "#FF7B00", chip: "bg-[#FFF1DC] text-[#8B1A1A]", border: "border-[#F0E2C0]" },
    cyan: { accent: "#06B6D4", chip: "bg-cyan-50 text-cyan-900", border: "border-cyan-200" },
    emerald: { accent: "#0FB28A", chip: "bg-emerald-50 text-emerald-900", border: "border-emerald-200" },
    yellow: { accent: "#FDC700", chip: "bg-yellow-50 text-amber-900", border: "border-yellow-200" },
    orange: { accent: "#F4751F", chip: "bg-orange-50 text-orange-900", border: "border-orange-200" },
  };
  const t = THEMES[theme] || THEMES.amber;
  const activeAll = useMemo(
    () => (docs || []).filter((d) => (d.status || "Active") === "Active"),
    [docs],
  );

  // Collect academic years that actually have docs. Default selection = current AY if it has docs, otherwise the most recent year with docs.
  const yearsAvailable = useMemo(() => {
    const set = new Set(activeAll.map((d) => (d.academic_year || "").trim()).filter(Boolean));
    return Array.from(set).sort().reverse(); // newest first
  }, [activeAll]);
  const currentAY = computeAcademicYear();
  const defaultYear = yearsAvailable.includes(currentAY)
    ? currentAY
    : yearsAvailable[0] || currentAY;
  const [selectedYear, setSelectedYear] = useState(defaultYear);

  const active = useMemo(
    () => activeAll.filter((d) => (d.academic_year || "") === selectedYear),
    [activeAll, selectedYear],
  );

  return (
    <div data-testid={`${testidPrefix}-page`}>
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Academic Year filter */}
        {activeAll.length > 0 && (
          <div className="mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid={`${testidPrefix}-filter-bar`}>
            <div className="flex items-center gap-3">
              <Calendar size={16} strokeWidth={1.5} style={{ color: t.accent }} />
              <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                Academic Year
              </span>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                data-testid={`${testidPrefix}-filter-select`}
                className="bg-white border border-slate-200 text-slate-900 text-sm px-3 py-2 rounded-sm focus:outline-none focus:ring-1 focus:border-slate-400"
                style={{ "--tw-ring-color": t.accent }}
              >
                {(yearsAvailable.length ? yearsAvailable : [currentAY]).map((y) => (
                  <option key={y} value={y}>
                    {y}
                    {y === currentAY ? " (current)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <span className="text-xs text-slate-500">
              {active.length} {active.length === 1 ? "document" : "documents"} in {selectedYear}
            </span>
          </div>
        )}

        {active.length === 0 ? (
          <div
            className={`bg-white border ${t.border} rounded-lg p-12 text-center`}
            data-testid={`${testidPrefix}-empty`}
          >
            <div
              className="mx-auto w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: `${t.accent}22`, color: t.accent }}
            >
              <FileText size={22} strokeWidth={1.5} />
            </div>
            <div className="mt-3 text-slate-600">
              {activeAll.length === 0
                ? "Academic documents will appear here once published by the institute."
                : `No documents available for academic year ${selectedYear} yet.`}
            </div>
          </div>
        ) : (
          <div className={`bg-white border ${t.border} rounded-lg overflow-hidden`} data-testid={`${testidPrefix}-list`}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 text-[11px] uppercase tracking-wider" style={{ background: `${t.accent}10` }}>
                  <th className="px-5 py-3 w-12">#</th>
                  <th className="px-5 py-3">Document Name</th>
                  <th className="px-5 py-3 text-right">Download</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {active.map((d, i) => (
                  <tr key={d.id} data-testid={`${testidPrefix}-row-${d.id}`} className="hover:bg-slate-50">
                    <td className="px-5 py-3.5 text-slate-500 font-medium">{i + 1}</td>
                    <td className="px-5 py-3.5">
                      <div className="font-medium text-slate-900">{d.name}</div>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      {d.pdf_url ? (
                        <a
                          href={d.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          data-testid={`${testidPrefix}-download-${d.id}`}
                          className="inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.1em] px-3 py-1.5 rounded-sm transition-colors"
                          style={{ background: t.accent, color: "white" }}
                          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
                          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                        >
                          <Download size={12} strokeWidth={2} /> Download PDF
                          <ExternalLink size={10} strokeWidth={2} />
                        </a>
                      ) : (
                        <span className="text-slate-400 italic text-xs">no file</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
